import api from './api';
import type { LearnerCourse } from '../types/learnerCourse';
import type { CatalogLiveCourse, OnDemandCourse } from '../types/catalog';
import { progressApi, type InProgressCourseItem } from './progressApi';
import { usersApi } from './usersApi';
import { courseTypeTags, credentialCodesFromAccreditations } from '../utils/credentialTypes';

// Cached user jurisdiction codes for matching against course credits
let userJurisdictionCodes: Set<string> | null = null;

async function getUserJurisdictionCodes(): Promise<Set<string>> {
  if (userJurisdictionCodes) return userJurisdictionCodes;

  try {
    const jurisdictions = await usersApi.getMyJurisdictions();
    userJurisdictionCodes = new Set(
      jurisdictions.map((j) => j.jurisdictionAbbr.toUpperCase())
    );
  } catch {
    // Don't cache empty set on error - allow retry next time
    return new Set();
  }

  return userJurisdictionCodes;
}

/**
 * Invalidate the cached user jurisdiction codes.
 * Call this when user updates their jurisdictions in Settings.
 */
export function invalidateJurisdictionCache(): void {
  userJurisdictionCodes = null;
}

function matchesUserJurisdiction(jurisdictionCode: string, codes: Set<string>): boolean {
  return codes.has(jurisdictionCode.toUpperCase());
}

// Backend response types
interface AssignmentDto {
  id: number;
  itemType: string;
  itemId: number;
  itemTitle: string;
  /** The kinds the assigned course holds, as canonical codes, alphabetical. Empty for pathways. */
  variantKinds: string[];
  /** Accreditation types the course grants on-demand, alphabetical. Empty for pathways. */
  accreditationTypes?: string[];
  durationMinutes: number;
  assignedAt: string;
  dueDate?: string;
  status: string;
  isOverdue: boolean;
  progress?: number;
}

// Convert assignment to LearnerCourse format for dashboard
function assignmentToLearnerCourse(a: AssignmentDto): LearnerCourse {
  return {
    id: String(a.itemId),
    title: a.itemTitle,
    duration: a.durationMinutes,
    // Pathway assignments carry no variant kinds of their own — chip the item type.
    ...(a.itemType === 'pathway'
      ? { credentialCodes: [], learningObjectTypeCode: 'pathway' }
      : courseTypeTags(a.accreditationTypes, a.variantKinds)),
    practiceAreas: [],
    credits: [],
    progress: a.progress ?? 0,
    status: a.status === 'Completed' ? 'completed' : 'in_progress',
    isBookmarked: false,
    assignedAt: a.assignedAt,
  };
}

// Convert an on-demand catalog course to LearnerCourse format for dashboard.
// The server returns only the credits of the course's on-demand delivery, so a
// dual-delivery course never surfaces its Live accreditation chip on this card.
function onDemandCourseToLearnerCourse(c: OnDemandCourse, jurisdictionCodes: Set<string>): LearnerCourse {
  return {
    id: String(c.learningObjectId),
    title: c.title,
    description: c.shortDescription || undefined,
    duration: c.durationMinutes,
    ...courseTypeTags(c.credits.map((credit) => credit.accreditationType), c.variantKinds),
    practiceAreas: c.practiceAreas,
    credits: c.credits.map((credit) => ({
      jurisdiction: credit.jurisdictionName, // Full name for flyout display
      jurisdictionCode: credit.jurisdictionCode, // Abbreviation for chip labels
      amount: credit.creditAmount,
      creditType: credit.creditType,
      credentialCode: credit.accreditationType.toLowerCase(),
      userMatch: matchesUserJurisdiction(credit.jurisdictionCode, jurisdictionCodes),
    })),
    progress: 0,
    status: 'not_started',
    thumbnailUrl: c.thumbnailUrl || undefined,
    isBookmarked: false,
    originalLiveDate: c.originalLiveDate,
  };
}

// Convert progress item to LearnerCourse format for dashboard
function progressItemToLearnerCourse(p: InProgressCourseItem): LearnerCourse {
  return {
    id: String(p.learningObjectId),
    title: p.title,
    duration: p.durationMinutes,
    ...courseTypeTags(p.accreditationTypes, p.variantKinds),
    practiceAreas: [],
    credits: [],
    progress: p.progressPercent,
    status: 'in_progress',
    thumbnailUrl: p.thumbnailUrl || undefined,
    isBookmarked: false,
    deliveryMode: p.deliveryMode,
    originalLiveDate: p.originalLiveDate ?? null,
  };
}

export interface EnrolledLiveWebinar {
  id: string;
  title: string;
  description?: string;
  scheduledAt: string;
  durationMinutes: number;
  timedDeliveryUrl: string | null;
  /** Credential type codes the live event offers (e.g. `['cle']`, `['cle', 'cpe']`). */
  credentialCodes: string[];
  /** CourseLiveEvent.Id — drives the Add to Calendar affordance. */
  liveEventId: number;
}

export const learnerCourseApi = {
  /**
   * Get courses currently in progress from both assignments API and progress API.
   * Merges admin-assigned and self-started courses, deduplicating by learningObjectId.
   */
  getInProgress: async (): Promise<LearnerCourse[]> => {
    const [assignmentResponse, progressCourses] = await Promise.all([
      api.get<{ items: AssignmentDto[] }>('/me/assignments?status=InProgress&limit=10'),
      progressApi.getInProgressCourses().catch(() => [] as InProgressCourseItem[]),
    ]);

    const assignmentCourses = assignmentResponse.data.items.map(assignmentToLearnerCourse);

    // Build set of (learningObjectId, deliveryMode) already in assignments to dedup
    const assignmentCourseIds = new Set(assignmentCourses.map(c => c.id));

    // Add self-started courses that don't have assignments
    // Same course can appear twice (once per delivery mode) — dedup by (learningObjectId + deliveryMode)
    const selfStartedCourses = progressCourses
      .filter(p => !assignmentCourseIds.has(String(p.learningObjectId)))
      .map(progressItemToLearnerCourse);

    return [...assignmentCourses, ...selfStartedCourses];
  },

  /**
   * Get upcoming live courses from the catalog live-courses endpoint.
   * Returns Beacon 360 live events scoped to the user's org libraries and personalized
   * to courses the learner can earn credit for (`forMe=true` — server applies the
   * jurisdiction × credential filter).
   */
  getUpcomingLive: async (): Promise<LearnerCourse[]> => {
    const [response, jurisdictionCodes] = await Promise.all([
      // BPE-732: ask for the five the tile renders. This used to fetch every
      // upcoming event in the caller's libraries and slice(0, 5) the result —
      // a multi-hundred-KB download on every dashboard load to show five cards.
      // Events come back soonest-first, so the server's five are the same five.
      api.get<CatalogLiveCourse[]>('/catalog/live-learning-objects?forMe=true&limit=5'),
      getUserJurisdictionCodes(),
    ]);

    return response.data.map((c): LearnerCourse => {
      const credentialCodes = credentialCodesFromAccreditations(c.accreditationTypes);
      const primaryCode = credentialCodes[0];

      // Prefer the new per-jurisdiction `credits[]` shape (correct creditType per row).
      // Fall back to the legacy parallel-array shape so this client still works against
      // older deployments — drop the fallback once all envs are on the new endpoint.
      const credits = c.credits && c.credits.length > 0
        ? c.credits.map((cr) => ({
            jurisdiction: cr.jurisdictionName, // full name for flyout
            jurisdictionCode: cr.jurisdictionCode,
            amount: cr.creditAmount,
            creditType: cr.creditType ?? 'General',
            credentialCode: cr.accreditationType?.toLowerCase() ?? primaryCode,
            userMatch: matchesUserJurisdiction(cr.jurisdictionCode, jurisdictionCodes),
          }))
        : c.jurisdictionCodes.map((code, index) => ({
            jurisdiction: c.jurisdictionNames[index] || code,
            jurisdictionCode: code,
            amount: c.creditHours ?? 0,
            creditType: c.accreditationTypes[0] ?? 'General',
            credentialCode: primaryCode,
            userMatch: matchesUserJurisdiction(code, jurisdictionCodes),
          }));

      return {
        id: String(c.learningObjectId),
        title: c.title,
        description: c.shortDescription ?? undefined,
        duration: c.durationMinutes,
        credentialCodes,
        practiceAreas: c.practiceAreas ?? [],
        credits,
        progress: 0,
        status: 'not_started',
        liveDateTime: c.scheduledAt,
        liveEventId: c.liveEventId,
        isBookmarked: false,
        deliveryMode: 'Live',
      };
    });
  },

  getEnrolledLive: async (): Promise<EnrolledLiveWebinar[]> => {
    // BPE-732: the server resolves enrolment now. This used to pull the whole
    // upcoming feed and keep the rows where isEnrolled was true — the same
    // multi-hundred-KB download as the tile above, on the same page load.
    const response = await api.get<CatalogLiveCourse[]>(
      '/catalog/live-learning-objects?enrolledOnly=true',
    );
    const seen = new Set<number>();
    return response.data
      // Redundant against a server that honours enrolledOnly, and the safe
      // failure mode against one that doesn't: an older deployment ignoring the
      // param would otherwise present every live course as one the learner is
      // registered for. Costs a pass over a handful of rows.
      .filter((c) => c.isEnrolled)
      // A course with several upcoming sessions appears once per session; keep
      // the soonest (the feed is ordered by scheduled time).
      .filter((c) => {
        if (seen.has(c.learningObjectId)) return false;
        seen.add(c.learningObjectId);
        return true;
      })
      .map((c) => {
        const codes = credentialCodesFromAccreditations(c.accreditationTypes);
        return {
          id: String(c.learningObjectId),
          title: c.title,
          description: c.shortDescription ?? undefined,
          scheduledAt: c.scheduledAt,
          durationMinutes: c.durationMinutes,
          timedDeliveryUrl: c.timedDeliveryUrl ?? null,
          credentialCodes: codes,
          liveEventId: c.liveEventId,
        };
      });
  },

  /**
   * Get the newest on-demand courses from the catalog API. The server only
   * returns courses that are actually on-demand ready (no upcoming live events,
   * a configured CheckpointMode, an active wistia delivery), scoped to the
   * learner's libraries and saved jurisdictions × credentials, newest
   * originalLiveDate first (BPE-404). It returns a bare array with no total, so
   * no count query runs for a tile that never shows one.
   */
  getNew: async (): Promise<LearnerCourse[]> => {
    const [response, jurisdictionCodes] = await Promise.all([
      api.get<OnDemandCourse[]>('/catalog/on-demand-learning-objects?limit=5'),
      getUserJurisdictionCodes(),
    ]);
    return response.data.map((c) => onDemandCourseToLearnerCourse(c, jurisdictionCodes));
  },

  /**
   * Get bookmarked courses from engagement API.
   */
  getBookmarked: async (): Promise<LearnerCourse[]> => {
    interface SavedCourse {
      learningObjectId: number;
      title: string;
      durationMinutes: number;
      variantKinds: string[];
      thumbnailUrl: string | null;
    }
    const response = await api.get<{ items: SavedCourse[] }>('/bookmarks/learning-objects?limit=10');
    return response.data.items.map((c): LearnerCourse => ({
      id: String(c.learningObjectId),
      title: c.title,
      duration: c.durationMinutes,
      // The saved-course payload carries no accreditations yet, so this card
      // resolves from the kinds step of the shared rule.
      ...courseTypeTags(null, c.variantKinds),
      practiceAreas: [],
      credits: [],
      progress: 0,
      status: 'not_started',
      thumbnailUrl: c.thumbnailUrl || undefined,
      isBookmarked: true,
    }));
  },
};
